import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAngularContext } from 'AngularContext';
import { targetBrandConfig } from 'AppBranding';
import { type CurrentUser, programInclusionsForTranscriptDownload } from 'Users';
import { Text } from 'Text';
import { InlineLink } from 'InlineLink';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { type DownloadableStudentDocsHelperClass } from '../editDocuments.types';

// Lets a current or graduated learner download their unofficial institutional transcript. The
// download itself is still handled by the Angular DownloadableStudentDocsHelper, reached through the
// injector (it streams the document and triggers the browser download).
export function UnofficialTranscriptDownloadSection({ currentUser }: { currentUser: CurrentUser }) {
    const { t } = useTranslation('back_royal');
    const $injector = useAngularContext();
    const [downloading, setDownloading] = useState(false);

    const institutionName = targetBrandConfig(currentUser)?.brandNameShort ?? '';

    const downloadTranscript = async () => {
        const HelperClass = $injector?.get<DownloadableStudentDocsHelperClass>('DownloadableStudentDocsHelper');
        if (!HelperClass) return;

        setDownloading(true);
        try {
            const cohortIds = programInclusionsForTranscriptDownload(currentUser).map(
                programInclusion => programInclusion.cohortId,
            );
            await new HelperClass().downloadTranscript(currentUser, cohortIds, {
                format: 'digital',
                type: 'unofficial',
                studentInitiated: true,
            });
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="flex flex-col gap-2 py-6">
            <Text as="h2" color="black" weight="semibold" className="text-base">
                {t('settings.edit_documents.unofficial_transcript_download_section_title', { institutionName })}
            </Text>

            <Text as="p" variant="small">
                <Trans
                    t={t}
                    i18nKey="settings.edit_documents.unofficial_transcript_download_section_description"
                    values={{ institutionName }}
                    components={{
                        download: <InlineLink className="text-xs" onPress={() => void downloadTranscript()} />,
                    }}
                />
            </Text>

            {downloading ? <FrontRoyalSpinner /> : null}
        </div>
    );
}
